import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { useAccounts } from "../settings/queries.js";
import { useEnvironment } from "../../config/environment-context.js";
import { AccountSelector } from "../settings/account-selector.js";
import { Button } from "../../shared/ui/button.js";
import { ResourceState } from "../../shared/ui/resource-state.js";
import { isMonthlyStrategy, lifecycleActions } from "./models.js";
import { ProblemNotice } from "./problem-notice.js";
import { useBot, useBotAction, useBotRevisions, useDuplicateBot } from "./queries.js";
import { BotBacktestsPanel } from "./bot-backtests-panel.js";
import { BotDecisionTreePanel } from "./bot-decision-tree.js";

export function BotDetailPage({ botId }: { readonly botId: string }) {
  const environment = useEnvironment();
  const navigate = useNavigate();
  const bot = useBot(botId);
  const revisions = useBotRevisions(botId);
  const accounts = useAccounts();
  const action = useBotAction(botId);
  const duplicate = useDuplicateBot();
  const [startOpen, setStartOpen] = useState(false);
  const [accountId, setAccountId] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [lastOperation, setLastOperation] = useState<{ id: string; status: string } | null>(null);

  if (bot.isLoading || revisions.isLoading)
    return (
      <ResourceState
        kind="loading"
        title="Loading bot"
        description="Loading lifecycle state and immutable revisions."
      />
    );
  if (bot.error !== null || revisions.error !== null)
    return (
      <ResourceState
        kind="error"
        title="Bot unavailable"
        description="TradingEngine could not return this Bot."
        action={
          <Link className="button button--quiet" to="/bots">
            Return to bots
          </Link>
        }
      />
    );
  if (bot.data === undefined)
    return (
      <ResourceState
        kind="not-found"
        title="Bot not found"
        description="This Bot is unavailable."
      />
    );

  const current = bot.data;
  const activeRevision =
    revisions.data?.find((revision) => revision.id === current.activeRevisionId) ??
    revisions.data?.[0];
  const actions = lifecycleActions(current.state);
  const backtestOnly = isMonthlyStrategy(activeRevision?.strategy);
  const selectedAccount = accountId === "" ? (current.liveBrokerAccountId ?? "") : accountId;
  const issueAction = async (requestedAction: "archive" | "ready" | "start" | "stop") => {
    const operation = await action.mutateAsync({
      action: requestedAction,
      ...(requestedAction === "start" ? { brokerAccountId: selectedAccount } : {}),
      expectedVersion: current.version,
    });
    setLastOperation({ id: operation.id, status: operation.status });
    setStartOpen(false);
  };

  return (
    <section className="page-stack" aria-labelledby="bot-title">
      <header className="page-header page-header--action">
        <div>
          <p className="eyebrow">{current.symbol}</p>
          <h1 id="bot-title">{current.name}</h1>
          <p>
            Version {current.version} · active revision {activeRevision?.revision ?? "unavailable"}
          </p>
        </div>
        <span className={`status-pill status-pill--${current.state}`}>{current.state}</span>
      </header>

      <div className="content-card section-stack">
        <div className="connection-heading">
          <div>
            <h2>Lifecycle controls</h2>
            <p className="section-description">
              Commands are idempotent, version checked, and tracked as durable backend operations.
            </p>
          </div>
          <div className="button-row">
            {["draft", "ready", "stopped", "error"].includes(current.state) ? (
              <Link className="button button--quiet" to={`/bots/${current.id}/edit`}>
                Edit
              </Link>
            ) : null}
            <Button
              disabled={duplicate.isPending || activeRevision === undefined}
              onClick={() =>
                activeRevision === undefined
                  ? undefined
                  : void duplicate
                      .mutateAsync({ botName: current.name, revision: activeRevision })
                      .then(() => navigate("/bots"))
              }
              tone="quiet"
            >
              {duplicate.isPending ? "Duplicating…" : "Duplicate"}
            </Button>
            {actions.includes("ready") ? (
              <Button disabled={action.isPending} onClick={() => void issueAction("ready")}>
                Mark ready
              </Button>
            ) : null}
            {actions.includes("start") ? (
              <Button
                disabled={action.isPending || backtestOnly || !environment.VITE_ENABLE_LIVE_TRADING}
                onClick={() => setStartOpen(true)}
                title={
                  backtestOnly
                    ? "Monthly trend/cash is backtest-only."
                    : environment.VITE_ENABLE_LIVE_TRADING
                      ? undefined
                      : "Live trading is disabled for this deployment."
                }
              >
                Start live
              </Button>
            ) : null}
            {actions.includes("stop") ? (
              <Button disabled={action.isPending} onClick={() => void issueAction("stop")}>
                Stop
              </Button>
            ) : null}
            {actions.includes("archive") ? (
              <Button
                disabled={action.isPending}
                onClick={() => void issueAction("archive")}
                tone="quiet"
              >
                Archive
              </Button>
            ) : null}
          </div>
        </div>
        {lastOperation === null ? null : (
          <div className="operation-status" aria-live="polite">
            <strong>Operation {lastOperation.status}</strong>
            <span>{lastOperation.id}</span>
          </div>
        )}
        {!environment.VITE_ENABLE_LIVE_TRADING && actions.includes("start") ? (
          <p className="field-hint">
            Live trading is disabled by this deployment's isolated feature configuration.
          </p>
        ) : null}
        {backtestOnly ? (
          <p className="field-hint">
            Monthly trend/cash is backtest-only; live execution is not supported.
          </p>
        ) : null}
        {action.error === null ? null : <ProblemNotice error={action.error} />}
        {duplicate.error === null ? null : <ProblemNotice error={duplicate.error} />}
      </div>

      {startOpen ? (
        <div
          className="content-card live-confirmation"
          role="dialog"
          aria-labelledby="live-confirmation-title"
        >
          <div>
            <p className="eyebrow">Real orders</p>
            <h2 id="live-confirmation-title">Confirm live activation</h2>
            <p>
              Starting this Bot can submit stock orders to the selected Tastytrade account.
              TradingEngine will run readiness and risk checks before activation.
            </p>
          </div>
          <AccountSelector
            accounts={accounts.data ?? []}
            id="start-account"
            onChange={setAccountId}
            value={selectedAccount}
          />
          <label className="check-field">
            <input
              checked={confirmed}
              onChange={(event) => setConfirmed(event.currentTarget.checked)}
              type="checkbox"
            />
            <span>
              <strong>I understand this can place live orders</strong>
              <small>The account, Bot revision, and command will be recorded.</small>
            </span>
          </label>
          <div className="button-row">
            <Button
              disabled={!confirmed || selectedAccount === "" || action.isPending}
              onClick={() => void issueAction("start")}
            >
              {action.isPending ? "Starting…" : "Confirm and start"}
            </Button>
            <Button disabled={action.isPending} onClick={() => setStartOpen(false)} tone="quiet">
              Cancel
            </Button>
          </div>
        </div>
      ) : null}

      <div className="detail-grid">
        <div className="content-card section-stack">
          <h2>Active configuration</h2>
          {activeRevision === undefined ? (
            <ResourceState
              kind="degraded"
              title="Revision unavailable"
              description="TradingEngine did not return the active configuration."
            />
          ) : (
            <dl className="definition-grid">
              <div>
                <dt>Strategy</dt>
                <dd>
                  {activeRevision.strategy.kind} v{activeRevision.strategy.version}
                </dd>
              </div>
              <div>
                <dt>Position sizing</dt>
                <dd>
                  {activeRevision.positionSizing.kind}: {activeRevision.positionSizing.value}
                </dd>
              </div>
              <div>
                <dt>Shorting</dt>
                <dd>{activeRevision.risk.allowShort ? "Allowed" : "Disabled"}</dd>
              </div>
              <div>
                <dt>Schedule</dt>
                <dd>
                  {activeRevision.schedule.exchange} ·{" "}
                  {activeRevision.schedule.regularSessionOnly
                    ? "regular session"
                    : "extended session"}
                </dd>
              </div>
              <div>
                <dt>Execution</dt>
                <dd>
                  {activeRevision.execution.defaultOrderType} ·{" "}
                  {activeRevision.execution.timeInForce}
                </dd>
              </div>
              <div>
                <dt>Live account</dt>
                <dd>{current.liveBrokerAccountId ?? "Not assigned"}</dd>
              </div>
            </dl>
          )}
        </div>
        <div className="content-card section-stack">
          <h2>Revision history</h2>
          <ol className="revision-list">
            {(revisions.data ?? []).map((revision) => (
              <li key={revision.id}>
                <div>
                  <strong>Revision {revision.revision}</strong>
                  {revision.id === current.activeRevisionId ? (
                    <span className="status-pill status-pill--ready">active</span>
                  ) : null}
                </div>
                <span>
                  {revision.createdAt === ""
                    ? "Timestamp unavailable"
                    : new Date(revision.createdAt).toLocaleString()}
                </span>
                <small>{revision.id}</small>
              </li>
            ))}
          </ol>
        </div>
      </div>
      <BotDecisionTreePanel botId={current.id} />
      <div id="backtests">
        <BotBacktestsPanel bot={current} revision={activeRevision} />
      </div>
    </section>
  );
}
